<script setup>
import { ref, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import SectionLayout from './SectionLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import CatalogBannersAPI from 'dashboard/api/catalogBanners';

const isLoading = ref(false);
const banners = ref([]);
const isUploading = ref(false);

const loadBanners = async () => {
  isLoading.value = true;
  try {
    const response = await CatalogBannersAPI.get();
    banners.value = response.data;
  } catch (error) {
    console.error('Error loading banners:', error);
  } finally {
    isLoading.value = false;
  }
};

const addBanner = async event => {
  const file = event.target.files[0];
  if (!file) return;

  isUploading.value = true;
  try {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('active', true);
    await CatalogBannersAPI.create(formData);
    await loadBanners();
    useAlert('Banner adicionado com sucesso!');
  } catch (error) {
    console.error('Error adding banner:', error);
    useAlert('Erro ao adicionar banner');
  } finally {
    isUploading.value = false;
    event.target.value = '';
  }
};

const toggleBanner = async banner => {
  try {
    const formData = new FormData();
    formData.append('active', !banner.active);
    await CatalogBannersAPI.update(banner.id, formData);
    await loadBanners();
  } catch (error) {
    console.error('Error toggling banner:', error);
    useAlert('Erro ao atualizar banner');
  }
};

const removeBanner = async banner => {
  try {
    await CatalogBannersAPI.delete(banner.id);
    await loadBanners();
    useAlert('Banner removido com sucesso!');
  } catch (error) {
    console.error('Error removing banner:', error);
    useAlert('Erro ao remover banner');
  }
};

const moveBanner = async (banner, direction) => {
  const idx = banners.value.findIndex(b => b.id === banner.id);
  const swapIdx = idx + direction;
  if (swapIdx < 0 || swapIdx >= banners.value.length) return;

  const other = banners.value[swapIdx];
  try {
    const fd1 = new FormData();
    fd1.append('position', other.position);
    const fd2 = new FormData();
    fd2.append('position', banner.position);
    await Promise.all([
      CatalogBannersAPI.update(banner.id, fd1),
      CatalogBannersAPI.update(other.id, fd2),
    ]);
    await loadBanners();
  } catch (error) {
    console.error('Error reordering banners:', error);
  }
};

const updateBannerLink = async (banner, link) => {
  try {
    const formData = new FormData();
    formData.append('link', link);
    await CatalogBannersAPI.update(banner.id, formData);
  } catch (error) {
    console.error('Error updating link:', error);
  }
};

onMounted(() => {
  loadBanners();
});
</script>

<template>
  <SectionLayout
    title="Banners do Catalogo"
    description="Adicione banners promocionais que serao exibidos em carousel no topo do catalogo publico."
    with-border
  >
    <div v-if="isLoading" class="flex justify-center py-8">
      <span class="i-lucide-loader-2 animate-spin text-2xl text-n-slate-10" />
    </div>

    <div v-else class="space-y-4">
      <!-- Upload Button -->
      <div class="flex items-center gap-3">
        <label
          class="flex cursor-pointer items-center gap-2 rounded-lg bg-n-blue-9 px-4 py-2 text-sm font-semibold text-white hover:bg-n-blue-10 transition-colors"
          :class="{ 'opacity-50 pointer-events-none': isUploading }"
        >
          <span
            :class="
              isUploading
                ? 'i-lucide-loader-2 animate-spin'
                : 'i-lucide-plus'
            "
          />
          {{ isUploading ? 'Enviando...' : 'Adicionar Banner' }}
          <input
            type="file"
            accept="image/*"
            class="hidden"
            :disabled="isUploading"
            @change="addBanner"
          />
        </label>
        <span class="text-xs text-n-slate-9">
          Recomendado: 1200x400px, JPG ou PNG
        </span>
      </div>

      <!-- Empty State -->
      <div
        v-if="banners.length === 0"
        class="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-n-weak py-12"
      >
        <span class="i-lucide-image text-4xl text-n-slate-8 mb-3" />
        <p class="text-sm text-n-slate-9">
          Nenhum banner adicionado. Adicione banners para exibir no catalogo.
        </p>
      </div>

      <!-- Banner List -->
      <div v-else class="grid gap-3">
        <div
          v-for="(banner, index) in banners"
          :key="banner.id"
          class="flex items-center gap-3 rounded-xl border border-n-weak p-3 transition-colors"
          :class="{ 'opacity-50': !banner.active }"
        >
          <!-- Thumbnail -->
          <div
            class="h-16 w-28 shrink-0 overflow-hidden rounded-lg border border-n-weak bg-n-alpha-2"
          >
            <img
              v-if="banner.image_url"
              :src="banner.image_url"
              alt="Banner"
              class="h-full w-full object-cover"
            />
            <div
              v-else
              class="flex h-full w-full items-center justify-center"
            >
              <span class="i-lucide-image text-xl text-n-slate-8" />
            </div>
          </div>

          <!-- Info -->
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2 mb-1">
              <span
                class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                :class="
                  banner.active
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-n-alpha-2 text-n-slate-9'
                "
              >
                {{ banner.active ? 'Ativo' : 'Inativo' }}
              </span>
              <span class="text-xs text-n-slate-8">
                #{{ index + 1 }}
              </span>
            </div>
            <input
              :value="banner.link || ''"
              type="text"
              placeholder="Link (opcional)"
              class="w-full rounded border border-n-weak bg-n-solid-1 px-2 py-1 text-xs text-n-slate-12"
              @change="updateBannerLink(banner, $event.target.value)"
            />
          </div>

          <!-- Actions -->
          <div class="flex shrink-0 items-center gap-1">
            <button
              class="rounded-lg p-1.5 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
              title="Mover para cima"
              :disabled="index === 0"
              :class="{ 'opacity-30': index === 0 }"
              @click="moveBanner(banner, -1)"
            >
              <span class="i-lucide-chevron-up text-base" />
            </button>
            <button
              class="rounded-lg p-1.5 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
              title="Mover para baixo"
              :disabled="index === banners.length - 1"
              :class="{ 'opacity-30': index === banners.length - 1 }"
              @click="moveBanner(banner, 1)"
            >
              <span class="i-lucide-chevron-down text-base" />
            </button>
            <button
              class="rounded-lg p-1.5 text-n-slate-10 hover:bg-n-alpha-2"
              :title="banner.active ? 'Desativar' : 'Ativar'"
              @click="toggleBanner(banner)"
            >
              <span
                :class="
                  banner.active
                    ? 'i-lucide-eye text-green-500'
                    : 'i-lucide-eye-off text-n-slate-8'
                "
                class="text-base"
              />
            </button>
            <button
              class="rounded-lg p-1.5 text-n-ruby-9 hover:bg-red-50 dark:hover:bg-red-900/20"
              title="Remover"
              @click="removeBanner(banner)"
            >
              <span class="i-lucide-trash-2 text-base" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </SectionLayout>
</template>
